import React from "react";
import { Box, Paper, Stack, Typography, Chip } from "@mui/material";
import { contentRead } from "../../services/content";

type Props = { form: any; branch?: string };

function guessImageMimeType(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase();
  if (!ext) return "application/octet-stream";
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "webp") return "image/webp";
  if (ext === "gif") return "image/gif";
  if (ext === "svg") return "image/svg+xml";
  return "application/octet-stream";
}

const ProductPreview: React.FC<Props> = ({ form, branch }) => {
  const title =
    form?.locales?.tr?.title ||
    form?.locales?.en?.title ||
    form?.slug ||
    "Önizleme";
  const image =
    form?.media?.thumbnail || form?.media?.images?.[0] || "/uploads/AREL.png";
  const [resolvedImage, setResolvedImage] = React.useState<string>(image);
  const fireRating = form?.fireRating;
  const category = form?.category;

  React.useEffect(() => {
    setResolvedImage(image);

    if (!image) return;
    if (image.startsWith("data:") || image.startsWith("blob:") || image.startsWith("http")) {
      return;
    }

    const pathNoQuery = image.split("?")[0].split("#")[0];
    if (!pathNoQuery.startsWith("/")) return;
    const relPath = pathNoQuery.replace(/^\//, "");

    // Only attempt resolving for static public assets we store in repo.
    if (!relPath.startsWith("data/") && !relPath.startsWith("uploads/")) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await contentRead(relPath, branch);
        const base64 = (res.content || "").replace(/\n/g, "").trim();
        if (!base64) {
          if (!cancelled && res.download_url) setResolvedImage(res.download_url);
          return;
        }
        const mime = guessImageMimeType(relPath);
        const dataUrl = `data:${mime};base64,${base64}`;
        if (!cancelled) setResolvedImage(dataUrl);
      } catch {
        // Keep original image path
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [image, branch]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={2}>
        <Box
          sx={{
            width: 96,
            height: 96,
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={resolvedImage}
            alt={title}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {fireRating && <Chip label={fireRating} size="small" />}
            {category && (
              <Chip label={category} size="small" variant="outlined" />
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ProductPreview;
